import BackIcon from "@ecologi/assets/icons/back-icon.svg";
import {
  BasketLink,
  Button,
  IconLink,
  ImpactShopCheckoutModal,
  SignUpSignInModal,
  NumberField,
  TopNavigation,
  buildDataUrlFromBase64,
  GroupSelector,
} from "@ecologi/react-components/src";
import { desktopUp } from "@ecologi/react-components/src/theme/ecologi-breakpoints";
import {
  ImpactShopBasketProduct,
  PAYMENT_TYPE,
  User,
  guuid,
  penceToDisplayPrice,
  locationCurrencyCodeMapper,
  isImpactFundProductType,
  FUNDED_BY_NAME_MAX_LENGTH,
  pluralise,
  PlatformFeature,
  MIN_ORDER_VALUES,
  BAFTA_FUND_SLUG,
  isValidEmailAddress,
} from "@ecologi/shared";
import Image from "next/image";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { useIsomorphicLayoutEffect } from "react-use";
import { toast } from "react-toastify";
import useMedia from "react-use/lib/useMedia";
import { Head } from "../../../components";
import {
  createBasketPayment,
  formatAPIErrorForView,
  getMockedImpact,
  pushImpactShopSuccess,
  pushImpactShopProductRemovedFromBasket,
  pushImpactShopBasketLoggedInCheckout,
  pushImpactShopBasketLoggedOutCheckout,
} from "../../../lib";
import { useAnalytics } from "../../../lib/analytics/useAnalytics";
import { useGetConfigCatValue } from "../../../lib/configcat";
import { useStore } from "../../../store";
import { useFetchImpactShopStore } from "../../../store/impact-shop";
import { useConfirmPaymentIntent } from "../../profile/hooks/use-confirm-payment-intent";
import { IMPACT_SHOP_MESSAGES } from "../constants";
import { useImpactShopMessage } from "../hooks";
import { useListImpactGroups } from "../hooks/useListImpactGroups";
import { useCreateImpactGroup } from "../hooks/useCreateImpactGroup";
import styled from "@emotion/styled";
import {
  CheckoutButtonContainer,
  ContinueButton,
  CentredBadge,
  Footer,
  FooterContent,
  FooterWrapper,
  HeaderRow,
  ProductImageContainer,
  ProductInformationContainer,
  ItemRow,
  ItemContent,
  ItemHeader,
  ItemName,
  ItemsList,
  ItemsNotAvailable,
  ItemsTotal,
  QuantityWrapper,
  RemoveLinkContainer,
  Subtotal,
  Title,
  TotalWrapper,
  UnitPriceWrapper,
  Wrapper,
  ProductDetailsContainer,
  PriceDetailsContainer,
  ProductInlineFieldContainer,
} from "./basket-page.styles";
import { BillingDetailsManagement } from "./BillingDetailsManagement";
import { TextField } from "@ecologi/react-components/src/TextField";
import { ProductInlineFieldEditor } from "./ProductInlineFieldEditor";
import { useBasketEditor } from "./useBasketEditor";
import { RemovalReasonModal, RemovalReasonId } from "./removal-reason-modal";

const useImpactShopStore = useFetchImpactShopStore();

const BasketProductImage = styled(Image)`
  object-fit: "cover";
`;

export function BasketPage(props: {
  accessToken: string;
  impactGroupsEnabled?: boolean;
}) {
  const {
    basket,
    currency,
    emptyBasket,
    removeProductFromBasket,
    setProductQuantityAs,
    setProductFundedByAs,
    setProductGroupAs,
    setProductRecipientEmailAs,
    totalCostOfProduct,
    totalNumberOfProductsInBasket,
    totalPriceOfBasket,
    unitPriceOfProduct,
    basketProductsWithCurrency,
    setCurrency,
  } = useImpactShopStore();

  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isSignUpSignInModalOpen, setIsSignUpSignInModalOpen] = useState(false);
  const [isRemovalModalOpen, setIsRemovalModalOpen] = useState(false);
  const [productToRemove, setProductToRemove] =
    useState<ImpactShopBasketProduct | null>(null);
  const [selectedRemovalReason, setSelectedRemovalReason] = useState<
    RemovalReasonId | ""
  >("");
  const { confirmPaymentIntent } = useConfirmPaymentIntent();
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [taxCode, setTaxCode] = useState("");

  const { openEditor, closeEditor, isEditorOpen, updateValue, getValue } =
    useBasketEditor();

  const router = useRouter();
  const { user, setUser, location } = useStore();
  const isUserCardAvailable = user?.hasCard() || false;
  const isLoggedIn = !!user;

  const [paymentMode, setPaymentMode] = useState<PAYMENT_TYPE>(
    user?.isPayByInvoiceCustomer ? PAYMENT_TYPE.INVOICE : PAYMENT_TYPE.CARD
  );
  const [isReady, setIsReady] = useState(false);
  const [basketQty, setBasketQty] = useState(0);
  const { sendAnalyticsEvent } = useAnalytics();

  const [impactGroupsEnabledFromConfig] = useGetConfigCatValue(
    PlatformFeature.ImpactGroupsEnabled,
    false
  );
  const impactGroupsEnabled =
    props.impactGroupsEnabled ?? impactGroupsEnabledFromConfig;

  const [sendAsGiftEnabled] = useGetConfigCatValue(
    PlatformFeature.ImpactShopSendAsGift,
    false
  );

  const { data: groups = [] } = useListImpactGroups(
    impactGroupsEnabled && isLoggedIn
  );

  const createGroup = useCreateImpactGroup();

  const resetIdempotencyKey = () => setIdempotencyKey(guuid());

  useIsomorphicLayoutEffect(() => {
    setIsReady(true);
  }, []);

  useEffect(() => {
    setCurrency(locationCurrencyCodeMapper[location.countryCode]);
  }, [location]);

  useIsomorphicLayoutEffect(() => {
    setBasketQty(totalNumberOfProductsInBasket());
  }, [basket]);

  useEffect(() => {
    resetIdempotencyKey();
  }, [user]);

  const isDesktopUpScreen = useMedia(desktopUp);
  const showBasketItems = isReady && !!basket.length;

  const IMAGE_DIMENSION = 138;

  useImpactShopMessage();

  const handleQuantityBounds = (bound: "above" | "below") => {
    const message =
      bound === "above"
        ? IMPACT_SHOP_MESSAGES.UpdateStockCapacityExceed
        : IMPACT_SHOP_MESSAGES.UpdateStockCapacityBelow;
    toast.error(message.body);
  };

  const { utmParams } = useImpactShopStore();

  const handleConfirmPayment = useCallback(async () => {
    try {
      // currency is passed in the payload but not actually used yet
      // because impact orders are all processed using the user's specific currency
      const { products, currency } = basketProductsWithCurrency();

      const paymentResponse = await createBasketPayment(
        products,
        currency,
        paymentMode,
        poNumber,
        taxCode,
        idempotencyKey,
        props.accessToken,
        utmParams
      );

      if (paymentResponse.status === "requires_action") {
        const clientSecret = paymentResponse.clientSecret;
        const result = await confirmPaymentIntent(clientSecret);
        if (!result.success) {
          resetIdempotencyKey();
          throw new Error("3DS authentication failed for the card provided");
        }
      }

      const userWithMockedImpact = await getMockedImpact(
        paymentMode,
        idempotencyKey,
        props.accessToken
      );

      const analyticItems = basket.map((product) => ({
        productId: product.id,
        productName: product.display.title,
        quantity: product.quantity,
      }));

      // Our prices are stored as 470 but we need to send 4.70 for reporting purposes
      const totalPriceForReporting = totalPriceOfBasket() / 100;

      pushImpactShopSuccess({
        revenue: totalPriceForReporting,
        currency: currency,
        id: idempotencyKey,
        items: analyticItems,
      });
      sendAnalyticsEvent("impactShopPurchase", {
        revenue: totalPriceForReporting,
        currency: currency,
      });

      setUser(new User(userWithMockedImpact));
      await router.push(`/impact-shop/checkout-success?type=${paymentMode}`);

      emptyBasket();
    } catch (error) {
      resetIdempotencyKey();
      toast.error(formatAPIErrorForView(error));
    } finally {
      setIsCheckoutModalOpen(false);
    }
  }, [
    idempotencyKey,
    poNumber,
    taxCode,
    paymentMode,
    props.accessToken,
    sendAnalyticsEvent,
    utmParams,
  ]);

  const handleRemoveClick = (product: ImpactShopBasketProduct) => {
    setProductToRemove(product);
    setSelectedRemovalReason("");
    setIsRemovalModalOpen(true);
  };

  const handleReasonSelected = (
    removalReason: RemovalReasonId = RemovalReasonId.NO_REASON_GIVEN
  ) => {
    removeProductFromBasket(productToRemove);
    setIsRemovalModalOpen(false);

    if (removalReason !== RemovalReasonId.NO_REASON_GIVEN) {
      toast.success("Thank you for your feedback");
    }

    pushImpactShopProductRemovedFromBasket({
      productName: productToRemove.display.title,
      quantity: productToRemove.quantity,
    });

    sendAnalyticsEvent("impactShopRemoveFromBasket", {
      productSlug: productToRemove.slug,
      quantity: productToRemove.quantity,
    });

    sendAnalyticsEvent("impactShopRemovalReason", {
      reason: removalReason,
      productSlug: productToRemove.slug,
    });

    setProductToRemove(null);
  };

  const handleReasonModalClose = () => {
    handleReasonSelected();
  };

  const getBasketItemKey = (product: ImpactShopBasketProduct) =>
    `${product.id}-${product.fundedBy || user?.username}-${
      product.groupId || "no-group"
    }-${product.recipientEmail || "no-recipient"}`;

  const handleQuantityChange = (
    product: ImpactShopBasketProduct,
    newValue: string | number
  ) => {
    const currentQuantity = product.quantity;
    const newQuantity = Number(newValue);
    const quantityDiff = newQuantity - currentQuantity;

    setProductQuantityAs(product, newQuantity);

    if (currentQuantity !== newQuantity) {
      if (quantityDiff === 1) {
        sendAnalyticsEvent("impactShopBasketQuantityIncrease", {
          productSlug: product.slug,
        });
      } else if (quantityDiff === -1) {
        sendAnalyticsEvent("impactShopBasketQuantityDecrease", {
          productSlug: product.slug,
        });
      } else {
        sendAnalyticsEvent("impactShopBasketQuantityEdit", {
          productSlug: product.slug,
        });
      }
    }
  };

  const renderPriceDetails = (product: ImpactShopBasketProduct) => {
    const isImpactFund = isImpactFundProductType(product.productType);
    const isImpactFundWithUnitType = isImpactFund && product.display.unitType;

    const unitPriceDefault = (
      <ItemContent use="body3">
        {penceToDisplayPrice(unitPriceOfProduct(product), currency)}
      </ItemContent>
    );

    const showDesktopUpScreen = isReady && isDesktopUpScreen;
    const showUnitPriceHeader = !showDesktopUpScreen;
    const showTotalHeader = !showDesktopUpScreen;

    return (
      <>
        <QuantityWrapper>
          <NumberField
            variant="basic"
            label="quantity"
            min={product.minQuantity}
            max={product.maxQuantity}
            value={product.quantity}
            onBoundsError={handleQuantityBounds}
            name="quantity"
            onChange={(e) => {
              handleQuantityChange(product, e.target.value);
            }}
            readOnly={false}
          />
        </QuantityWrapper>

        <UnitPriceWrapper data-testid="basket-item-unit-price">
          {showUnitPriceHeader && (
            <ItemHeader use="body3">Unit price</ItemHeader>
          )}
          {isImpactFund && !isImpactFundWithUnitType ? "-" : unitPriceDefault}
        </UnitPriceWrapper>
        <TotalWrapper data-testid="basket-item-subtotal">
          {showTotalHeader && <ItemHeader use="body3">Total</ItemHeader>}
          <ItemContent use="body3">
            {penceToDisplayPrice(totalCostOfProduct(product), currency)}
          </ItemContent>
        </TotalWrapper>
      </>
    );
  };

  const renderItemsTotal = () => (
    <TotalWrapper data-testid="basket-total">
      <FooterContent use="body2">
        {penceToDisplayPrice(totalPriceOfBasket(), currency)}
      </FooterContent>
    </TotalWrapper>
  );

  const renderBasketItemCount = () => {
    const total = totalNumberOfProductsInBasket();
    return <ItemsTotal>{`(${pluralise(total, "item", true)})`}</ItemsTotal>;
  };

  const getPayText = (paymentType: PAYMENT_TYPE) =>
    paymentType === PAYMENT_TYPE.CARD
      ? `Pay ${penceToDisplayPrice(totalPriceOfBasket(), currency)} now`
      : `Request invoice for ${penceToDisplayPrice(
          totalPriceOfBasket(),
          currency
        )}`;

  const getMinimumText = () =>
    `Minimum ${penceToDisplayPrice(
      MIN_ORDER_VALUES[currency],
      currency
    )} to checkout`;

  const isBaftaOnlyBasket =
    basket.length > 0 && basket.every((item) => item.slug === BAFTA_FUND_SLUG);

  const isBelowMinimum =
    !isBaftaOnlyBasket && totalPriceOfBasket() < MIN_ORDER_VALUES[currency];

  const getButtonText = () => {
    if (isBelowMinimum) return getMinimumText();
    return user ? getPayText(paymentMode) : "Go to checkout";
  };

  const isCheckoutDisabled =
    isBelowMinimum ||
    (user &&
      (!user.hasAddress() ||
        (!isUserCardAvailable && paymentMode === PAYMENT_TYPE.CARD)));

  const handleCheckoutClick = () => {
    const basketItems = basket.map((product) => product.display.title);
    const basketPrice = penceToDisplayPrice(totalPriceOfBasket(), currency);

    if (user) {
      setIsCheckoutModalOpen(true);
      pushImpactShopBasketLoggedInCheckout({ basketPrice, basketItems });
      sendAnalyticsEvent("impactShopBasketPayNow", {
        basketPrice,
        currency: currency,
      });
    } else {
      setIsSignUpSignInModalOpen(true);
      pushImpactShopBasketLoggedOutCheckout({ basketPrice, basketItems });
      sendAnalyticsEvent("impactShopBasketGoToCheckout", {
        basketPrice,
        currency: currency,
      });
    }
  };

  return (
    <>
      <Head title={"Shopping Basket"} />
      <TopNavigation
        leftSlot={
          <IconLink label="Shop" href="/impact-shop" startSlot={<BackIcon />} />
        }
        loggedOutNav={!props.accessToken}
        rightSlot={
          <BasketLink href="/impact-shop/basket" itemQuantity={basketQty} />
        }
      />
      <Wrapper>
        <Title use="componentTitle" as="h2">
          Shopping basket
        </Title>
        <div>
          {showBasketItems ? (
            <>
              <HeaderRow as="div">
                <ItemHeader use="body3">Item</ItemHeader>
                {isReady && isDesktopUpScreen && (
                  <>
                    <QuantityWrapper>
                      <ItemHeader use="body3">Quantity</ItemHeader>
                    </QuantityWrapper>
                    <UnitPriceWrapper>
                      <ItemHeader use="body3">Unit price</ItemHeader>
                    </UnitPriceWrapper>
                    <TotalWrapper>
                      <ItemHeader use="body3">Subtotal</ItemHeader>
                    </TotalWrapper>
                  </>
                )}
              </HeaderRow>
              <ItemsList>
                {isReady &&
                  basket.map((product) => (
                    <ItemRow
                      key={getBasketItemKey(product)}
                      data-testid="basket-item"
                    >
                      <ProductDetailsContainer>
                        <ProductImageContainer>
                          <BasketProductImage
                            alt={`Example ${product.display.title} project`}
                            blurDataURL={buildDataUrlFromBase64(
                              product.display.images[0].placeholder
                            )}
                            placeholder="blur"
                            src={product.display.images[0].url}
                            width={IMAGE_DIMENSION}
                            height={IMAGE_DIMENSION}
                          />
                          {product.display.badgePath && (
                            <CentredBadge>
                              <Image
                                src={product.display.badgePath}
                                alt={`${product.display.title} badge`}
                                layout="fill"
                              />
                            </CentredBadge>
                          )}
                        </ProductImageContainer>
                        <ProductInformationContainer>
                          <ItemName use="body2" as="h3">
                            <strong>{product.display.title}</strong>
                          </ItemName>
                          <ProductInlineFieldContainer>
                            <ProductInlineFieldEditor
                              label="Funded by"
                              value={product.fundedBy || user?.username || ""}
                              isEditing={isEditorOpen(
                                getBasketItemKey(product),
                                "fundedBy"
                              )}
                              onEdit={() =>
                                openEditor(
                                  getBasketItemKey(product),
                                  "fundedBy",
                                  product.fundedBy
                                )
                              }
                              onSave={() => {
                                setProductFundedByAs(
                                  product,
                                  getValue<string>() || ""
                                );
                                closeEditor();
                              }}
                              onCancel={closeEditor}
                              renderEditor={
                                <TextField
                                  name="fundedBy"
                                  label="Funded by"
                                  placeholder="Enter a 'funded by' tag"
                                  isLabelHidden={true}
                                  dense
                                  onChange={(e) => updateValue(e.target.value)}
                                  maxLength={FUNDED_BY_NAME_MAX_LENGTH}
                                  value={getValue<string>() || ""}
                                  autoComplete="off"
                                />
                              }
                              addButtonText="Add funded by"
                              testIdPrefix="funded-by"
                            />

                            {sendAsGiftEnabled && (
                              <ProductInlineFieldEditor
                                label="Recipient email"
                                value={product.recipientEmail || ""}
                                isEditing={isEditorOpen(
                                  getBasketItemKey(product),
                                  "recipientEmail"
                                )}
                                onEdit={() =>
                                  openEditor(
                                    getBasketItemKey(product),
                                    "recipientEmail",
                                    product.recipientEmail
                                  )
                                }
                                onSave={() => {
                                  sendAnalyticsEvent(
                                    "impactShopBasketRecipientEmailEdit",
                                    {
                                      productSlug: product.slug,
                                    }
                                  );
                                  const email = getValue<string>() || "";
                                  if (email && !isValidEmailAddress(email)) {
                                    return;
                                  }
                                  setProductRecipientEmailAs(
                                    product,
                                    email || null
                                  );
                                  closeEditor();
                                }}
                                onCancel={closeEditor}
                                renderEditor={
                                  <TextField
                                    name="recipientEmail"
                                    label="Recipient email"
                                    placeholder="Enter recipient email address"
                                    isLabelHidden={true}
                                    dense
                                    onChange={(e) =>
                                      updateValue(e.target.value)
                                    }
                                    value={getValue<string>() || ""}
                                    autoComplete="off"
                                    type="email"
                                    isInvalid={
                                      getValue<string>() &&
                                      !isValidEmailAddress(
                                        getValue<string>() || ""
                                      )
                                    }
                                    errorText={
                                      getValue<string>() &&
                                      !isValidEmailAddress(
                                        getValue<string>() || ""
                                      )
                                        ? "Please enter a valid email address"
                                        : undefined
                                    }
                                  />
                                }
                                addButtonText="Add recipient email"
                                testIdPrefix="recipient-email"
                              />
                            )}

                            {impactGroupsEnabled && (
                              <ProductInlineFieldEditor
                                label="Group"
                                value={
                                  product.groupId
                                    ? groups.find(
                                        (g) => g.id === product.groupId
                                      )?.name || "Unknown"
                                    : ""
                                }
                                isEditing={isEditorOpen(
                                  getBasketItemKey(product),
                                  "group"
                                )}
                                onEdit={() =>
                                  openEditor(
                                    getBasketItemKey(product),
                                    "group",
                                    product.groupId ?? null
                                  )
                                }
                                onSave={() => {
                                  setProductGroupAs(
                                    product,
                                    getValue<number | null>()
                                  );
                                  closeEditor();
                                }}
                                onCancel={closeEditor}
                                renderEditor={
                                  <GroupSelector
                                    groupId={getValue<number | null>() ?? null}
                                    groups={groups}
                                    isDisabled={false}
                                    isLoggedIn={isLoggedIn}
                                    onCreateGroup={createGroup}
                                    onGroupChange={(groupId) =>
                                      updateValue(groupId)
                                    }
                                    showHelpText={false}
                                    showLabel={false}
                                    width="220px"
                                  />
                                }
                                addButtonText="Add group"
                                testIdPrefix="group"
                              />
                            )}

                            <RemoveLinkContainer>
                              <Button
                                data-testid="remove-item"
                                onClick={() => handleRemoveClick(product)}
                                text="Remove item"
                                textWithUnderline
                              />
                            </RemoveLinkContainer>
                          </ProductInlineFieldContainer>
                        </ProductInformationContainer>
                      </ProductDetailsContainer>
                      {isReady && isDesktopUpScreen ? (
                        renderPriceDetails(product)
                      ) : (
                        <PriceDetailsContainer>
                          {renderPriceDetails(product)}
                        </PriceDetailsContainer>
                      )}
                    </ItemRow>
                  ))}
              </ItemsList>
            </>
          ) : (
            <>
              <ItemsNotAvailable use="body2">
                You have no items in your shopping basket.
              </ItemsNotAvailable>
              <ContinueButton
                href="/impact-shop"
                text={"Continue shopping"}
                primary
              />
            </>
          )}
        </div>

        {showBasketItems && (
          <>
            <FooterWrapper>
              <Footer>
                <Subtotal>
                  <FooterContent use="body2">
                    Total {renderBasketItemCount()}
                  </FooterContent>
                  {renderItemsTotal()}
                </Subtotal>
              </Footer>
            </FooterWrapper>
            {user && (
              <>
                <BillingDetailsManagement
                  paymentType={paymentMode}
                  setPaymentType={setPaymentMode}
                  poNumber={poNumber}
                  setPoNumber={setPoNumber}
                  taxCode={taxCode}
                  setTaxCode={setTaxCode}
                />
              </>
            )}

            <CheckoutButtonContainer>
              <Button
                disabled={isCheckoutDisabled}
                onClick={handleCheckoutClick}
                text={getButtonText()}
                submit
                primary
              />
            </CheckoutButtonContainer>
          </>
        )}
        {!user && (
          <SignUpSignInModal
            isOpen={isSignUpSignInModalOpen}
            onClose={() => setIsSignUpSignInModalOpen(false)}
            signInText="Sign in to add impact to your profile"
            signUpText="Create an account to purchase impact"
            signInHref="/api/login?redirectTo=/impact-shop/basket"
            signUpHref="/pay-as-you-go"
            onSignInClick={() => {
              sendAnalyticsEvent("impactShopBasketSignInClick");
            }}
            onSignUpClick={() => {
              sendAnalyticsEvent("impactShopBasketRegisterClick");
            }}
          />
        )}
        {user && (
          <ImpactShopCheckoutModal
            formattedTotalCost={penceToDisplayPrice(
              totalPriceOfBasket(),
              currency
            )}
            handleConfirmPayment={handleConfirmPayment}
            paymentType={paymentMode}
            isModalOpen={isCheckoutModalOpen}
            setIsModalOpen={setIsCheckoutModalOpen}
            basketItemCount={basket.length}
            maskedCardNumber={user?.maskedCardNumber}
          />
        )}
        <RemovalReasonModal
          isOpen={isRemovalModalOpen}
          onClose={handleReasonModalClose}
          onReasonSelected={handleReasonSelected}
          selectedReason={selectedRemovalReason}
          onReasonChange={setSelectedRemovalReason}
        />
      </Wrapper>
    </>
  );
}
